import type { AdminSettingKey } from '../types/adminSettings'

// Keys stored in schools.ai_response_policy. The openai-chat edge function
// maps each key to the actual instruction text sent to the AI.
export const AI_RESPONSE_POLICY_DEFAULT = 'think_first'

// The labels here are defaults only: admins can override each one in
// 管理画面 → システム管理 → AI回答方針, stored in admin_settings (see the
// setting keys below). Use useAiResponsePolicyOptions() to render them.
export const AI_RESPONSE_POLICY_OPTIONS = [
  { value: 'think_first', label: '答えを教えず、生徒に考えさせる（聞かれたらヒントを出す）' },
  { value: 'hint_first', label: '基本は生徒に考えさせるが、最初からヒントを提示する' },
  { value: 'direct', label: '通常のAIと同様に、聞かれたことに回答する' },
] as const

export type AiResponsePolicy = typeof AI_RESPONSE_POLICY_OPTIONS[number]['value']

export interface AiResponsePolicyOption {
  value: AiResponsePolicy
  label: string
}

// Built-in instruction text per policy, used when the admin-edited row in
// admin_settings is missing, blank, or unreadable. The openai-chat edge function keeps
// its own copy of these strings because Deno cannot import from src/ — keep the two in
// sync, the same arrangement as supabase/functions/_shared/aiModels.ts.
export const AI_RESPONSE_POLICY_DEFAULT_INSTRUCTIONS: Record<AiResponsePolicy, string> = {
  think_first: '答えを直接教えず、生徒が自分で考えを深められるように問いかけで導いてください。生徒からヒントを求められた場合は、答えそのものではなくヒントを提示してください。',
  hint_first: '基本的には答えを直接教えず生徒に考えさせますが、最初から考えるためのヒントを提示してください。',
  direct: '通常のAIアシスタントと同様に、聞かれたことに直接回答してください。',
}

// admin_settings keys holding the admin-edited title / AI instruction per policy
export const AI_RESPONSE_POLICY_SETTING_KEYS: Record<
  AiResponsePolicy,
  { label: AdminSettingKey; instruction: AdminSettingKey }
> = {
  think_first: {
    label: 'ai_response_policy_think_first_label',
    instruction: 'ai_response_policy_think_first_instruction',
  },
  hint_first: {
    label: 'ai_response_policy_hint_first_label',
    instruction: 'ai_response_policy_hint_first_instruction',
  },
  direct: {
    label: 'ai_response_policy_direct_label',
    instruction: 'ai_response_policy_direct_instruction',
  },
}

// Legacy rows may hold free text; fall back to the default for unknown values
export const normalizeAiResponsePolicy = (value: string | null | undefined): AiResponsePolicy => {
  const match = AI_RESPONSE_POLICY_OPTIONS.find((option) => option.value === value)
  return match ? match.value : AI_RESPONSE_POLICY_DEFAULT
}
